/**
 * The entity detail page skeleton.
 *
 * Every detail screen in Orion — client, policy, claim, insurer — is built from these
 * pieces in the same order, so opening any of them presents the same shape:
 *
 *   breadcrumb → identity + actions → metrics → tabs → workspace
 *
 * which answers, in reading order: who is this, what state is it in, what matters, what
 * can I do, everything else. The consistency is structural rather than a convention
 * someone has to remember: a page cannot put its tabs above its metrics without visibly
 * fighting the components.
 *
 * Two rules are enforced here rather than left to each page:
 *
 * 1. **Metrics are not cards.** `DetailMetrics` renders a flat strip of label/value
 *    pairs. Six dashboard tiles at the top of a record is a summary of nothing — the
 *    figures belong to one entity, and boxing each one implies they are independent.
 *
 * 2. **Sections separate with space, not chrome.** `Section` draws no border and no
 *    surface. A card is reserved for content that is genuinely a discrete object
 *    (a document, a timeline entry, a nested table); grouping alone does not earn one.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';

/* ── page container ─────────────────────────────────────────────────────────── */

/**
 * Owns the vertical rhythm between the four bands. Pages never set these gaps
 * themselves, which is what keeps two detail screens from drifting a few pixels apart.
 */
export function DetailPage({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-col', className)}>{children}</div>;
}

/* ── 1 & 2 & 3: breadcrumb, identity, quick actions ─────────────────────────── */

export interface DetailCrumb {
  label: string;
  to?: string;
}

/**
 * One metadata item on the identity line — the facts that qualify the title.
 * A `to` turns it into a link, which is how a policy names its client without needing
 * a "related records" section for the one relationship that always matters.
 */
export interface DetailMeta {
  key: string;
  label: React.ReactNode;
  to?: string;
}

export function DetailHeader({
  breadcrumbs,
  avatar,
  title,
  /** Status badges. Sit on the title line: state is part of the identity, not a detail. */
  status,
  meta,
  actions,
  className,
}: {
  breadcrumbs: DetailCrumb[];
  avatar?: React.ReactNode;
  title: React.ReactNode;
  status?: React.ReactNode;
  meta?: DetailMeta[];
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-7', className)}>
      <nav aria-label="Breadcrumb" className="mb-3">
        <ol className="text-muted-soft flex flex-wrap items-center gap-1 text-[12.5px]">
          {breadcrumbs.map((crumb, index) => (
            <li key={index} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="size-3.5 opacity-40" />}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-foreground transition-colors duration-150">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-muted-foreground max-w-[24ch] truncate font-medium sm:max-w-none">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>

      {/* Actions share the title's row and never drift into the content below it. */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="flex min-w-0 items-start gap-3.5">
          {avatar}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1 className="min-w-0 truncate text-[24px] leading-tight font-semibold sm:text-[28px]">
                {title}
              </h1>
              {status}
            </div>

            {meta && meta.length > 0 && (
              <div className="text-muted-foreground mt-2 flex flex-wrap items-center text-[13.5px]">
                {meta.map((item, index) => (
                  <span key={item.key} className="flex items-center">
                    {index > 0 && <span className="text-muted-soft/60 px-2">·</span>}
                    {item.to ? (
                      <Link
                        to={item.to}
                        className="hover:text-foreground decoration-muted-soft/50 underline-offset-4 transition-colors duration-150 hover:underline"
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span className="flex items-center gap-1.5">{item.label}</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/* ── 4: summary metrics ─────────────────────────────────────────────────────── */

/**
 * A flat strip, not a row of tiles. Bounded above and below by a single hairline so it
 * reads as one band of figures belonging to the entity named above it.
 */
export function DetailMetrics({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <dl
      className={cn(
        'border-border mb-7 grid grid-cols-2 gap-x-8 gap-y-5 border-y py-4 sm:flex sm:flex-wrap sm:gap-x-10',
        className,
      )}
    >
      {children}
    </dl>
  );
}

export function Metric({
  label,
  value,
  /** Secondary line — a percentage, a countdown badge, a frequency. */
  hint,
  tone,
  /** Makes the whole metric a link to the tab or list that explains it. */
  to,
  isLoading,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'good' | 'warning' | 'critical';
  to?: string;
  isLoading?: boolean;
}) {
  const body = (
    <>
      <dt className="text-muted-soft text-[11.5px] font-medium">{label}</dt>
      <dd className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {isLoading ? (
          <Skeleton className="h-5 w-20" />
        ) : (
          <span
            className={cn(
              'text-[18px] leading-none font-semibold tracking-[-0.02em]',
              tone === 'good' && 'text-good',
              tone === 'warning' && 'text-serious',
              tone === 'critical' && 'text-critical',
            )}
          >
            {value}
          </span>
        )}
        {hint && <span className="text-muted-soft text-[12px] leading-none">{hint}</span>}
      </dd>
    </>
  );

  if (!to) return <div className="min-w-0">{body}</div>;

  return (
    <Link
      to={to}
      className="hover:bg-accent -mx-2 -my-1 min-w-0 rounded-lg px-2 py-1 transition-colors duration-150"
    >
      {body}
    </Link>
  );
}

/* ── 5: navigation ──────────────────────────────────────────────────────────── */

/**
 * Tabs sit flush to the page gutters — their bottom hairline is the rule between the
 * header band and the workspace, so no extra separator is needed.
 */
export function DetailTabsBar({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('mb-6', className)}>{children}</div>;
}

/* ── 6: workspace content ───────────────────────────────────────────────────── */

/**
 * A titled group of information. Draws no box: the heading plus the space around it is
 * the container. Sections are stacked by `SectionStack`, which owns the gap between them.
 */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('min-w-0', className)}>
      {(title || actions) && (
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="min-w-0">
            {/*
              A small uppercase eyebrow rather than a bold sentence-case heading. At the
              same size and weight as the values beneath it, a heading competes with the
              content it is supposed to be labelling; pulled back in size and colour but
              tracked out, it reads unmistakably as a marker and stops pulling attention.
              (Greek uppercases correctly here — the document is lang="el", so the engine
              drops the accents as it should.)

              "Pulled back in colour" had gone too far: at 11px in `--muted-foreground-soft`
              the eyebrow measured 2.83:1, so the one word telling you what you were
              looking at was the faintest thing in the band. It now takes the regular
              muted token (5.93:1) — still quiet next to a value, no longer straining. The
              size bump to 11.5px is for the same reason; uppercase at 11px with 0.07em
              tracking is the smallest text on the page and was carrying the most meaning.
            */}
            {title && (
              <h2 className="text-muted-foreground text-[11.5px] font-semibold tracking-[0.07em] uppercase">
                {title}
              </h2>
            )}
            {description && <p className="text-muted-soft mt-1.5 text-[12.5px]">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Vertical rhythm between sections.
 *
 * This was 56px, chosen when whitespace was the ONLY thing separating one section from
 * the next: field rows had no rules, so a 36px gap let two sections read as one
 * continuous list and 56px was what put the grouping beyond doubt.
 *
 * Field rows are hairline-separated now, and a section's last row ends its rules — so the
 * grouping is drawn rather than implied, and the gap no longer has to shout to be heard.
 * At 56px it had started to read as the page being half empty, which on a record holding
 * three short sections is the more expensive failure. 40px is still comfortably larger
 * than the 36px row pitch inside a section, so the hierarchy holds.
 */
export function SectionStack({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-10', className)}>{children}</div>;
}

/**
 * The workspace split: main column plus a supporting rail.
 *
 * The rail is for information that supports the case without being the case — a
 * timeline, a contact block, opportunities. It collapses below the main column on
 * narrow screens, which is the correct priority: supporting content should never be
 * what a user reads first.
 */
export function Workspace({
  children,
  aside,
  className,
}: {
  children: React.ReactNode;
  aside?: React.ReactNode;
  className?: string;
}) {
  if (!aside) return <div className={cn('min-w-0', className)}>{children}</div>;

  return (
    <div className={cn('grid min-w-0 gap-9 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]', className)}>
      <div className="min-w-0">{children}</div>
      <aside className="min-w-0 lg:border-border lg:border-l lg:pl-8">{aside}</aside>
    </div>
  );
}

/**
 * A bordered, unpadded surface for content that IS a discrete object — a nested table,
 * a list of documents. Deliberately not `Card`: no shadow, no elevation, because this
 * is a container for rows rather than a thing sitting on the page.
 */
export function Panel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('border-border bg-card overflow-hidden rounded-xl border', className)}>
      {children}
    </div>
  );
}

/** Detail-page loading state, shaped like the page it precedes. */
export function DetailPageSkeleton() {
  return (
    <div className="flex flex-col">
      <Skeleton className="mb-3 h-3.5 w-48" />
      <div className="mb-7 flex items-start gap-3.5">
        <Skeleton className="size-11 rounded-full" />
        <div className="flex-1 space-y-2.5">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-4 w-56" />
        </div>
      </div>
      <div className="border-border mb-7 flex flex-wrap gap-x-10 gap-y-5 border-y py-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-24" />
          </div>
        ))}
      </div>
      <Skeleton className="mb-6 h-10 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
